import * as React from 'react';

import { cn, Col, LocalizedRelativeDate, Row } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import { IConversation } from '../_state/_types';
import * as conversationListState from '../_state/conversationList.state';
import { useTranslations } from '../i18n/useTranslations';

type ChatCardProps = {
  conversation: IConversation;
};

export const ConversationCard = ({ conversation }: ChatCardProps) => {
  const t = useTranslations();

  const [selectedConversation, setSelectedConversation] = useAtom(
    conversationListState.$selectedConversation,
  );

  const isSelected = selectedConversation?.id === conversation.id;

  const lastMessageTime = React.useMemo(
    () =>
      new Date(conversation.lastMessage?.createdAt ?? conversation.createdAt),
    [conversation],
  );

  const lastMessageText = React.useMemo(
    () => conversation.lastMessage?.plaintext,
    [conversation],
  );

  return (
    <Col
      className={cn(
        '@ConversationCard',
        'hover:**:data-divider:opacity-0 hover:[&+div_[data-divider]]:opacity-0',
        isSelected && '[&+div_[data-divider]]:opacity-0',
      )}
    >
      <div
        data-divider
        className={cn(
          'mt-px w-full border-t border-space/20 opacity-0 transition-all',
          !isSelected && 'opacity-100',
        )}
      />
      <div
        className={cn(
          '@ChatCard',
          'cursor-pointer rounded-lg border-2 border-transparent p-2 pt-1.5 text-sm text-white transition-colors',
          'hover:bg-space/10',
          conversation.unreadMessageCount && 'bg-space/10 hover:bg-space/20',
          isSelected && 'bg-space! text-foreground',
        )}
        onClick={() => setSelectedConversation(conversation)}
      >
        <Col className="gap-1.5">
          <Col>
            {/* Subject and Time */}
            <Row xBetween gap2>
              {/* 🟠🟠🟠 Subject */}
              <div
                className={cn(
                  'truncate text-base font-medium capitalize',
                  !conversation.subject && 'font-normal',
                )}
              >
                {conversation.subject ??
                  t.conversationCard.untitledConversation}
              </div>
              {/* 🟠🟠🟠 Last message time */}
              <LocalizedRelativeDate
                date={lastMessageTime}
                className="shrink-0 whitespace-nowrap opacity-60"
              />
            </Row>

            {/* 🟠🟠🟠 Participants */}
            <div className="truncate opacity-60">
              {!conversation.participants &&
                t.conversationCard.loadingParticipants}
              {conversation.participants?.map((p) => p.user?.name).join(', ')}
            </div>
          </Col>

          <Row gap2 yEnd>
            {/* 🟠🟠🟠 Last message */}
            <div
              className={cn(
                'line-clamp-2 overflow-hidden font-light',
                !conversation.lastMessage && 'opacity-30',
              )}
            >
              {lastMessageText ?? t.conversationCard.noMessagesYet}
            </div>
            {/* 🟠🟠🟠 Unread count */}
            {conversation.unreadMessageCount > 1 && (
              <div
                className={
                  'ml-auto rounded bg-primary px-1.5 py-0.5 text-xs text-abs-white'
                }
              >
                {conversation.unreadMessageCount}
              </div>
            )}
          </Row>
        </Col>
      </div>
    </Col>
  );
};
